import { apiFetch } from './client';

export const IVR_NODE_TYPES = [
  // Content & navigation — 'menu' leads every node-type list (Sam 2026-08-06)
  'menu', 'playback',
  'series_folder', 'sequential_folder', 'unlock_series_folder', 'unlock_sequential_folder',
  'bulletin', 'latest_n_folder', 'rotation_folder', 'random_folder',
  'bookmark', 'return_to_bookmark',
  // Input & logic
  'data_collection', 'password',
  'label_route', 'add_label', 'remove_label',
  'modify_auth', 'modify_audio', 'playback_settings',
  // Communication & actions
  'record', 'voicemail', 'listen_voicemail', 'conference', 'transfer', 'ring_group',
  'dial', 'sip_connect', 'extension', 'notify_branch', 'public_forum',
  // Payments
  'payment', 'shopping',
  // Scheduling
  'day_of_week', 'switch_timer', 'timer', 'auto_rotate',
  // Education & engagement
  'enrollment', 'survey', 'quiz', 'points',
  // Admin & advanced
  'admin', 'jump', 'back', 'repeat', 'custom',
  // Auth & access
  'auth_list_signup',
] as const;

export type IvrNodeType = typeof IVR_NODE_TYPES[number];

/**
 * Semantic groups for the tree-builder node-type picker. Display order
 * + group labels live here so the picker shows a scannable categorized
 * list instead of a 44-long flat dropdown. Every value in
 * IVR_NODE_TYPES appears in exactly one group.
 */
export interface NodeTypeGroup {
  label: string;
  types: ReadonlyArray<{
    type: IvrNodeType;
    label: string;
    hint: string;
  }>;
}

export const IVR_NODE_TYPE_GROUPS: ReadonlyArray<NodeTypeGroup> = [
  {
    label: 'Content & navigation',
    types: [
      // 'menu' is deliberately first — it's the type tree authors reach
      // for most, so it leads every picker (Sam 2026-08-06).
      { type: 'menu',               label: 'Menu',                   hint: 'Prompt + DTMF branch' },
      { type: 'playback',           label: 'Playback',               hint: 'Play a recording to the caller' },
      { type: 'series_folder',      label: 'Series folder',          hint: 'Items in sort order + DTMF nav' },
      { type: 'sequential_folder',  label: 'Sequential Series',      hint: 'Caller picks start; playlist auto-advances forward or backward' },
      { type: 'unlock_series_folder', label: 'Unlock-series folder', hint: 'Series folder with calendar-based unlock schedule' },
      { type: 'unlock_sequential_folder', label: 'Unlock-sequential folder', hint: 'Sequential folder with calendar-based unlock schedule' },
      { type: 'bulletin',           label: 'Bulletin',               hint: 'Folder of announcements; newest plays first' },
      { type: 'latest_n_folder',    label: 'Latest folder',          hint: 'Plays the most recently uploaded recording with full playback controls' },
      { type: 'rotation_folder',    label: 'Rotation folder',        hint: 'One per call, cursor advances' },
      { type: 'random_folder',      label: 'Random folder',          hint: 'Random item per call' },
      { type: 'bookmark',           label: 'Bookmark',               hint: 'Save caller position' },
      { type: 'return_to_bookmark', label: 'Return to bookmark',     hint: 'Resume from saved position' },
    ],
  },
  {
    label: 'Input & logic',
    types: [
      { type: 'data_collection',    label: 'Data collection',        hint: 'Capture DTMF into a field' },
      { type: 'password',           label: 'Password gate',          hint: 'PIN check' },
      { type: 'label_route',        label: 'Label route',            hint: 'Branch by caller labels' },
      { type: 'add_label',          label: 'Add label',              hint: 'Tag the caller' },
      { type: 'remove_label',       label: 'Remove label',           hint: 'Untag the caller' },
      { type: 'modify_auth',        label: 'Modify auth',            hint: 'Clear caller identity' },
      { type: 'modify_audio',       label: 'Modify audio',           hint: 'Adjust playback prefs' },
      { type: 'playback_settings',  label: 'Playback settings menu', hint: 'Caller self-serves via DTMF' },
    ],
  },
  {
    label: 'Communication',
    types: [
      { type: 'record',             label: 'Record',                 hint: 'Capture caller voice' },
      { type: 'voicemail',          label: 'Voicemail (record)',     hint: 'Caller LEAVES a message in a box. Inbound: greeting → record → save.' },
      { type: 'listen_voicemail',   label: 'Voicemail (listen)',     hint: 'Caller LISTENS to messages in a box (inbox playback). Press keys to navigate.' },
      { type: 'conference',         label: 'Conference',             hint: 'Join a conference room' },
      { type: 'transfer',           label: 'Transfer',               hint: 'Route to tree / DID' },
      { type: 'ring_group',         label: 'Ring group',             hint: 'Bridge to group members' },
      { type: 'dial',               label: 'Dial',                   hint: 'Bridge to one target' },
      { type: 'sip_connect',        label: 'SIP connect',            hint: 'Bridge to a SIP URI' },
      { type: 'extension',          label: 'Extension',              hint: 'Bridge to SIP extension' },
      { type: 'notify_branch',      label: 'Notify (SMS)',           hint: 'Side-effect SMS to staff' },
      { type: 'public_forum',       label: 'Public forum',           hint: 'Post or listen (config.mode)' },
    ],
  },
  {
    label: 'Payments',
    types: [
      { type: 'payment',            label: 'Payment',                hint: 'Collect card + charge' },
      { type: 'shopping',           label: 'Shopping',               hint: 'Create cart for payment' },
    ],
  },
  {
    label: 'Scheduling',
    types: [
      { type: 'day_of_week',        label: 'Day of week',            hint: 'Branch by weekday' },
      { type: 'timer',              label: 'Time-of-day gate',       hint: 'Open-hours window' },
      { type: 'switch_timer',       label: 'Multi-window timer',     hint: 'Named time windows' },
      { type: 'auto_rotate',        label: 'Auto rotate',            hint: 'Round-robin slots' },
    ],
  },
  {
    label: 'Education & engagement',
    types: [
      { type: 'enrollment',         label: 'Enrollment',             hint: 'Enroll caller in a course' },
      { type: 'survey',             label: 'Survey',                 hint: 'Walk caller through questions' },
      { type: 'quiz',               label: 'Quiz',                   hint: 'Score-gated questions' },
      { type: 'points',             label: 'Points',                 hint: 'Award / debit ledger' },
    ],
  },
  {
    label: 'Flow control & advanced',
    types: [
      { type: 'jump',               label: 'Jump',                   hint: 'Advance to a specific node' },
      { type: 'back',               label: 'Back',                   hint: 'Pop navigation stack' },
      { type: 'repeat',             label: 'Repeat',                 hint: 'Replay current node' },
      { type: 'admin',              label: 'Admin marker',           hint: 'Log event + advance' },
      { type: 'custom',             label: 'Custom webhook',         hint: 'POST to URL; branch on status' },
    ],
  },
  {
    label: 'Auth & access',
    types: [
      { type: 'auth_list_signup',   label: 'Auth list signup',       hint: 'Register caller\'s number into an auth list' },
    ],
  },
];

export const AUTH_MODES = [
  'open', 'caller_id', 'pin', 'caller_id_and_pin', 'caller_id_or_pin',
] as const;

export type AuthMode = typeof AUTH_MODES[number];

/**
 * End-of-node behaviour. When a node finishes without an explicit `next`
 * edge the interpreter consults this to decide where the caller goes:
 *   - 'hunt_back' (default): return to the parent menu.
 *   - 'hangup': end the call.
 *   - 'custom_node': advance to `on_complete_node_id`.
 */
export const ON_COMPLETE_MODES = ['hunt_back', 'hangup', 'custom_node'] as const;
export type OnCompleteMode = typeof ON_COMPLETE_MODES[number];

export interface IvrNodeRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  tree_id: string;
  ref_number: string;
  node_type: IvrNodeType;
  name: string;
  notes: string | null;
  auth_mode: AuthMode;
  main_recording_id: string | null;
  intro1_recording_id: string | null;
  intro1_play_mode: string;
  intro2_recording_id: string | null;
  intro2_play_mode: string;
  outro_recording_id: string | null;
  /** Migration 0066. When false, DTMF is ignored during the slot's
   *  playback (FS playback_terminators=none + uuid_flush_dtmf after).
   *  Default true. Use for hard "you must hear this" intros. */
  intro1_skippable: boolean;
  intro2_skippable: boolean;
  outro_skippable: boolean;
  time_condition_id: string | null;
  /** Optional reference to a `time_frames` row (migration 0076). As of
   *  0078 this is a universal gate that applies to ANY node type. When
   *  set, the interpreter checks the frame BEFORE the handler runs
   *  and may short-circuit the node based on `time_frame_action`. */
  time_frame_id?: string | null;
  /** Migration 0078. Action when frame says not_in_frame. */
  time_frame_action?: 'hunt_back' | 'hangup' | 'route_to_node' | null;
  /** Migration 0078. Target node when action='route_to_node'. */
  time_frame_target_node_id?: string | null;
  /** Migration 0078. Optional recording played before the action. */
  time_frame_recording_id?: string | null;
  inline_time_settings: unknown;
  points_config: unknown;
  limits_config: unknown;
  config: unknown;
  fallback_node_id: string | null;
  active: boolean;
  position_x: string | null;
  position_y: string | null;
  max_reprompt: number;
  /** End-of-node behaviour (0034). */
  on_complete: OnCompleteMode;
  /** Target for on_complete='custom_node' (0035). */
  on_complete_node_id: string | null;
  /** Optional auth-list gate (0045). Empty array = no gate. */
  auth_list_ids: string[];
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * Config shape for `rotation_folder` nodes. Kept in sync with the engine's
 * RotationConfig (backend/src/freeswitch/engine/nodes/rotationFolder.ts)
 * and the zod schema in backend/src/ivrNodes/service.ts.
 */
export interface RotationFolderNodeConfig {
  folder_id?: string;
  rotation_interval_days?: number;
  /** Day-name strings ('mon'…'sun') as stored by the portal. */
  skip_days_of_week?: string[];
  /**
   * Manual shift of the rotation start point, in items. May be negative;
   * the engine wraps it with a double modulo. Written by the drawer's
   * "Start today from…" picker; 0 / absent = natural date-based position.
   */
  rotation_offset?: number;
}

/**
 * GET /ivr/nodes/:id/rotation-status response. Positions are 1-based.
 * `items` is the folder's active item list in the engine's play order —
 * the source for the "Start today from…" picker so portal positions always
 * match what callers hear.
 */
export interface RotationStatus {
  total: number;
  /** Item playing today (offset applied). Null when the folder is empty. */
  position: number | null;
  /** Item that would play today with rotation_offset = 0. Null when empty. */
  natural_position: number | null;
  /** Effective rotation_offset in the saved config. */
  offset: number;
  recording_id: string | null;
  recording_name: string | null;
  items: Array<{ position: number; recording_id: string; recording_name: string }>;
}

export interface CreateNodeBody {
  node_type: IvrNodeType;
  name: string;
  auth_mode?: AuthMode;
  notes?: string;
  main_recording_id?: string;
  intro1_recording_id?: string;
  intro2_recording_id?: string;
  outro_recording_id?: string;
  config?: Record<string, unknown>;
  max_reprompt?: number;
  fallback_node_id?: string;
}

export type UpdateNodeBody = Partial<
  Pick<
    IvrNodeRow,
    | 'node_type'
    | 'name'
    | 'notes'
    | 'auth_mode'
    | 'main_recording_id'
    | 'intro1_recording_id'
    | 'intro1_play_mode'
    | 'intro1_skippable'
    | 'intro2_recording_id'
    | 'intro2_skippable'
    | 'outro_skippable'
    | 'intro2_play_mode'
    | 'outro_recording_id'
    | 'fallback_node_id'
    | 'active'
    | 'max_reprompt'
    | 'on_complete'
    | 'on_complete_node_id'
  > & {
    config?: Record<string, unknown>;
    position_x?: number;
    position_y?: number;
    auth_list_ids?: string[];
    /** Migration 0076. Null clears the attachment; UUID attaches a frame. */
    time_frame_id?: string | null;
    /** Migration 0078. Gate action when frame says not_in_frame. */
    time_frame_action?: 'hunt_back' | 'hangup' | 'route_to_node' | null;
    /** Migration 0078. Target node when action='route_to_node'. */
    time_frame_target_node_id?: string | null;
    /** Migration 0078. Optional recording played before the action. */
    time_frame_recording_id?: string | null;
  }
>;

export const ivrNodes = {
  listForTree: (
    treeId: string,
    p: { search?: string; limit?: number; deleted?: boolean; customerId?: string | null } = {},
  ) => {
    const q = new URLSearchParams();
    if (p.search) q.set('search', p.search);
    if (p.limit) q.set('limit', String(p.limit));
    if (p.deleted) q.set('deleted', 'true');
    // Bugfix 2026-05-17: ref-form treeId (`IVT-7`) needs customer
    // scope so the backend resolves to the right customer's tree.
    if (p.customerId) q.set('customer_id', p.customerId);
    return apiFetch<{ rows: IvrNodeRow[] }>(`/api/ivr/trees/${treeId}/nodes?${q}`);
  },
  get: (id: string) => apiFetch<IvrNodeRow>(`/api/ivr/nodes/${id}`),
  /** Rotation-position readout for a rotation_folder node (saved config). */
  rotationStatus: (id: string) =>
    apiFetch<RotationStatus>(`/api/ivr/nodes/${id}/rotation-status`),
  create: (treeId: string, body: CreateNodeBody, opts: { customerId?: string | null } = {}) => {
    const q = new URLSearchParams();
    if (opts.customerId) q.set('customer_id', opts.customerId);
    const qs = q.toString();
    return apiFetch<IvrNodeRow>(`/api/ivr/trees/${treeId}/nodes${qs ? `?${qs}` : ''}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  update: (id: string, patch: UpdateNodeBody) =>
    apiFetch<IvrNodeRow>(`/api/ivr/nodes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  reparent: (id: string, body: { parent_node_id: string | null; branch_key?: string }) =>
    apiFetch<IvrNodeRow>(`/api/ivr/nodes/${id}/parent`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  remove: (id: string) =>
    apiFetch<void>(`/api/ivr/nodes/${id}`, { method: 'DELETE' }),
  /** Restore a soft-deleted node from the recycle bin. */
  restore: (id: string) =>
    apiFetch<{ row: IvrNodeRow; relinked: boolean }>(`/api/ivr/nodes/${id}/restore`, {
      method: 'POST',
    }),
  /** Permanently hard-delete a soft-deleted node (super_admin / reseller_owner only). */
  removePermanent: (id: string) =>
    apiFetch<void>(`/api/ivr/nodes/${id}?permanent=true`, { method: 'DELETE' }),
};
